import { get, reduce } from "lodash";
import { stringifyUrl } from "query-string";
import React, { useMemo } from "react";
import {
  matchPath,
  Redirect,
  Route,
  RouteComponentProps,
  Switch
} from "react-router-dom";
import CenteredLoading from "../../../components/CenteredLoading";
import DetailsPagination from "../../../components/DetailsPagination";
import DisplayLabels from "../../../components/labels/DisplayLabels";
import NavLink from "../../../components/NavLink";
import { useFilteredList } from "../../../context/filteredList";
import { getSmartLabelFromKey } from "../../../context/filteredList/FilteredListProvider";
import { useHotspotMakers } from "../../../context/hotspotMakers";
import { hotspotMakerMetadata } from "../../../context/hotspotMakers/HotspotMakersProvider";
import { avatarsApi, Entity } from "../../../utils/constants";
import { HotspotMaker } from "../../../utils/types";
import { LabelFilterKey } from "../../../utils/useFilteredLabels";
import MakerLinksTab from "./MakerLinksTab";
import StatisticsInfoTab from "./MakerStatisticsTab";
import MakerTweetsTab from "./MakerTweetsTab";

interface Props extends RouteComponentProps<{ id: string }> {}

const MakerDetails: React.FC<Props> = (props) => {
  const { hotspotMakersById, getHotspotMakerName } = useHotspotMakers();
  const { filteredList, smartLabelFilters } = useFilteredList();

  const filteredHotspotMakers = useMemo(
    () => filteredList[Entity.hotspotMaker],
    [filteredList]
  );

  const currentMaker = useMemo(
    () =>
      props.match.params.id
        ? hotspotMakersById[props.match.params.id]
        : undefined,
    [hotspotMakersById, props.match.params.id]
  );

  const detailsPageMatch = matchPath<{
    id: string;
    section: string;
  }>(props.location.pathname, {
    path: `/hotspots/makers/:id/:section`
  });

  const tabs = useMemo(() => {
    const allTabs = [
      {
        key: "statistics",
        title: "Statistics",
        hidden: false
      },
      {
        key: "links",
        title: "Links",
        hidden: false
      },
      {
        key: "tweets",
        title: "Tweets",
        hidden: !get(hotspotMakerMetadata, [
          props.match.params.id,
          "twitterLink"
        ])
      }
    ];

    return allTabs.filter(({ hidden }) => !hidden);
  }, [props.match.params.id]);

  if (!currentMaker) {
    return <CenteredLoading className="h-full" />;
  }

  return (
    <div className="pb-6">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="-mt-12 sm:-mt-16">
          <div className="flex justify-between">
            <img
              className="h-24 w-24 sm:h-32 sm:w-32 z-10 rounded-full"
              src={
                get(hotspotMakerMetadata, [props.match.params.id, "logoUrl"]) ||
                stringifyUrl({
                  url: `${avatarsApi}/initials/${getHotspotMakerName(
                    props.match.params.id
                  )}.svg`,
                  query: { w: 24, h: 24 }
                })
              }
              alt="Maker logo"
            />
            <div className="flex items-center">
              <DetailsPagination
                path="hotspots/makers"
                selectedItemUuid={props.match.params.id}
                data={filteredHotspotMakers}
                idKey="maker_id"
              />
            </div>
          </div>
          <div className="sm:flex-1 sm:min-w-0 sm:flex sm:items-center sm:justify-end sm:space-x-6 sm:pb-1">
            <div className="mt-6 min-w-0 flex-1">
              <div className="flex justify-between mb-2">
                <h1 className="text-md sm:text-2xl font-bold text-gray-900 dark:text-white truncate inline-flex items-center">
                  <span>{getHotspotMakerName(props.match.params.id)}</span>
                </h1>

                {get(hotspotMakerMetadata, [
                  props.match.params.id,
                  "purchaseLink"
                ]) && (
                  <button
                    className="px-2 py-1 inline-flex items-center justify-center border border-transparent text-base rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
                    onClick={() => {
                      window.open(
                        get(hotspotMakerMetadata, [
                          props.match.params.id,
                          "purchaseLink"
                        ])
                      );
                    }}
                  >
                    Purchase hotspots
                  </button>
                )}
              </div>

              <DisplayLabels
                className="mt-2"
                linkedUuid={currentMaker.maker_id}
                labels={reduce(
                  smartLabelFilters[Entity.hotspotMaker],
                  (
                    acc: string[],
                    filterFunction: (hotspotMaker: HotspotMaker) => boolean,
                    filterKey: string
                  ) => {
                    if (filterFunction(currentMaker)) {
                      acc.push(filterKey);
                    }
                    return acc;
                  },
                  []
                ).map((labelUuid) =>
                  getSmartLabelFromKey(labelUuid, Entity.hotspotMaker)
                )}
                filterKey={LabelFilterKey.mapHotspotMakers}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 sm:mt-2 2xl:mt-5">
        <div className="sm:border-b sm:border-gray-200 dark:border-gray-500 overflow-auto whitespace-nowrap">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <select
              className="sm:hidden block w-full pl-3 py-2 sm:text-sm rounded-md dark:bg-gray-800"
              onChange={(e) => {
                props.history.push(`${props.match.url}/${e.target.value}`);
              }}
              value={detailsPageMatch?.params.section}
            >
              {tabs.map(({ key, title }) => (
                <option key={key} value={key}>
                  {title}
                </option>
              ))}
            </select>
            <div className="hidden sm:block">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                {tabs.map(({ key, title }) => (
                  <NavLink
                    key={key}
                    to={`${props.match.url}/${key}`}
                    activeClassName="border-hotspotty-400 text-gray-900 dark:text-white dark:text-white dark:border-white"
                    inactiveClassName="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                    className="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm"
                  >
                    <span>{title}</span>
                  </NavLink>
                ))}
              </nav>
            </div>
          </div>
        </div>
      </div>

      <Switch>
        <Route
          path={`${props.match.path}/statistics`}
          component={StatisticsInfoTab as any}
        />
        <Route
          path={`${props.match.path}/tweets`}
          component={MakerTweetsTab as any}
        />
        <Route
          path={`${props.match.path}/links`}
          component={MakerLinksTab as any}
        />
        <Redirect to={`${props.match.path}/statistics`} />
      </Switch>
    </div>
  );
};

export default MakerDetails;
